import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { faNoteSticky } from "@fortawesome/pro-solid-svg-icons";
import { faCheckDouble } from "@fortawesome/pro-solid-svg-icons/faCheckDouble";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import PermissionType from "@mapmycustomers/shared/enum/PermissionType";
import {
  Company,
  Deal,
  EntitySupportingCard,
  EntityType,
  Person,
} from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import hasPermissionForEntityType from "@mapmycustomers/shared/util/hasPermissionForEntityType";

import { ActionBarItemType } from "@app/component/ActionsBar";
import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import FrequencyIcon from "@app/component/frequency/FrequencyIcon";
import { initFrequencyModal } from "@app/store/frequency/actions";
import { hasPermission } from "@app/store/iam";
import useClickTrackerContext from "@app/util/contexts/useClickTrackerContext";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import Actions from "./Actions";
import messages from "./messages";

const useCommonActions = (
  currentUser: User,
  entity: EntitySupportingCard,
  handleQuickActionButtonClick: (info: MenuInfo) => void,
  onEdit: (() => void) | undefined,
  onInitFrequencyModal: typeof initFrequencyModal.request,
  onShowCreateActivityModal: typeof showGlobalCreateActivityModal
) => {
  const intl = useIntl();
  const clickTrackerContext = useClickTrackerContext();
  const notesField = getFieldModelByEntityType(entity.entity).getByName(CommonFieldName.NOTES);
  const disableNoteEditing = !notesField?.isEditable;

  const handleManageFrequencies = useCallback(() => {
    clickTrackerContext.click(Actions.MANAGE_FREQUENCIES);
    onInitFrequencyModal({ entity });
  }, [clickTrackerContext, entity, onInitFrequencyModal]);

  const handleCreateActivity = useCallback(() => {
    clickTrackerContext.click(Actions.ADD_ACTIVITY);
    let account: Company | undefined;
    let contact: Person | undefined;
    let deal: Deal | undefined;

    if (entity.entity === EntityType.COMPANY) {
      account = entity as Company;
    } else if (entity.entity === EntityType.PERSON) {
      contact = entity as Person;
      account = (contact.primaryAccount ?? contact.accounts[0]) as Company | undefined;
    } else if (entity.entity === EntityType.DEAL) {
      deal = entity as Deal;
      contact = deal.contact;
      account = deal.account;
    }
    const now = new Date();

    onShowCreateActivityModal({
      activity: {
        account,
        contact,
        deal,
        startAt: now.toISOString(),
      },
    });
  }, [clickTrackerContext, entity, onShowCreateActivityModal]);

  const hasUpdatePermission = hasPermissionForEntityType(
    useSelector(hasPermission),
    PermissionType.UPDATE,
    entity.entity
  );
  // Per-record access gates editing; fall back to the global permission when accessStatus is absent.
  const hasEditAccess = entity.accessStatus?.update ?? hasUpdatePermission;

  const canCreateActivity = useSelector(hasPermission)(PermissionName.ACTIVITY_CREATE);

  const commonActions = useMemo(() => {
    const actions: ActionBarItemType[] = [
      {
        // The API gates notes on the role permission, not on per-record access.
        disabled: !hasUpdatePermission || disableNoteEditing,
        icon: <FontAwesomeIcon icon={faNoteSticky} />,
        key: Actions.ADD_NOTE,
        label: intl.formatMessage(messages.addNote),
        onClick: handleQuickActionButtonClick,
      },
      {
        disabled: !canCreateActivity,
        icon: <FontAwesomeIcon icon={faCheckDouble} />,
        key: Actions.ADD_ACTIVITY,
        label: intl.formatMessage(messages.addActivity),
        onClick: handleCreateActivity,
      },
      {
        disabled: !hasEditAccess,
        icon: <FrequencyIcon disabled={!hasEditAccess} size="tiny" />,
        key: Actions.MANAGE_FREQUENCIES,
        label: intl.formatMessage(messages.manageFrequencies),
        onClick: handleManageFrequencies,
      },
    ];
    if (onEdit) {
      actions.push({
        disabled: !hasEditAccess,
        icon: <FontAwesomeIcon icon={faPen} />,
        key: Actions.EDIT_ENTITY,
        label: intl.formatMessage(messages.edit),
        onClick: onEdit,
      });
    }
    return actions;
  }, [
    canCreateActivity,
    disableNoteEditing,
    handleCreateActivity,
    handleManageFrequencies,
    handleQuickActionButtonClick,
    hasEditAccess,
    hasUpdatePermission,
    intl,
    onEdit,
  ]);

  return commonActions;
};

export default useCommonActions;
